/**
 * DOM wiring for the redesigned post-purchase revision page.
 *
 * Only ever runs when the URL carries ?client-id= - the pre-purchase upload flow in
 * sections/image-upload.liquid returns before calling init(), and the markup this file
 * drives stays `hidden`.
 *
 * What is here today: loading the book, rendering the pages, the zoom viewer, and the two
 * behaviours the old page already had - "Request changes" (to the designers) and
 * "Approve", plus the self-service colour and name flows.
 */
(function () {
  'use strict';

  var R = window.RevisionPage || null;

  var state = {
    apiBase: null,
    clientId: null,
    settings: {},
    preview: null,
    pageUrls: [],
    zoomIndex: 0,
    openBackdrop: null,
    lastFocus: null
  };

  function $(id) {
    return document.getElementById(id);
  }

  function init(options) {
    if (!$('tmt-revision')) return;

    state.apiBase = options.apiBase;
    state.clientId = options.clientId;
    state.settings = options.settings || {};

    // Dev affordance: ?api_base= points the page at a local stand-in for the OP API (see
    // .claude/features/customer-revision-page/dev-mock-api.js), so the redesign can be
    // looked at without a real order - and without the live API being one careless click
    // away. Gated on the hostname, so on the storefront the parameter does nothing.
    var apiOverride = new URLSearchParams(window.location.search).get('api_base');
    if (apiOverride && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
      state.apiBase = apiOverride;
      console.info('[revision-page] using dev API base:', apiOverride);
    }

    $('tmt-revision').hidden = false;

    bindModalDismissal();
    bindZoom();
    bindChooser();
    bindDesignerRequest();
    bindApprove();
    bindWholeBook();
    bindFix();

    loadPreview();
  }

  /* ---------------------------------------------------------------- load */

  function loadPreview() {
    return fetch(state.apiBase + 'preview-images?client_id=' + encodeURIComponent(state.clientId))
      .then(function (res) {
        if (res.status === 410) throw new Error('expired');
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        // A whole-book revision is running: the old row is already gone and the new one
        // does not exist yet, so there is nothing to draw for a few minutes. Say that,
        // rather than the generic "we couldn't load your book" this used to fall into.
        if (data && data.status === 'regenerating') {
          showRegenerating();
          return;
        }

        // The endpoint 410s on expiry, but the old page also checked the timestamp
        // itself (image-upload.liquid:846) because a preview can lapse between the row
        // being read and the S3 signature running out. Keep both checks.
        var now = Math.floor(Date.now() / 1000);
        if (data.expires_at && data.expires_at <= now) throw new Error('expired');

        // The link may be an older revision of a book that has since been redrawn. The
        // server follows it forward and answers with the current one, so adopt that id
        // here: everything after this point (Approve, Fix, another revision) has to act on
        // the book actually on screen. The address bar is corrected too, so a bookmark or
        // a second refresh lands directly.
        if (data.client_id && data.client_id !== state.clientId) {
          state.clientId = data.client_id;
          var current = new URL(window.location.href);
          current.searchParams.set('client-id', data.client_id);
          window.history.replaceState({}, '', current.toString());
        }

        state.preview = data;
        state.pageUrls = Array.isArray(data.image_urls) ? data.image_urls : [];

        applyChildName(data.child_name);
        renderPages(state.pageUrls);
        applyOrderState(data);
      })
      .catch(function (err) {
        showLoadFailure(err.message === 'expired');
      });
  }

  function applyChildName(name) {
    var safeName = name || '';

    Array.prototype.forEach.call(document.querySelectorAll('[data-tmt-rev-name]'), function (el) {
      el.textContent = safeName;
    });

    // The heading is a merchant-editable string with a {child_name} placeholder, so it is
    // rendered from its template rather than patched in place - re-running this after a
    // name change must not compound. With no name the markup's own fallback stands.
    var heading = document.querySelector('[data-tmt-rev-title]');
    if (heading && safeName) {
      var template = heading.getAttribute('data-template') || '';
      if (template) heading.textContent = template.replace('{child_name}', safeName);
    }
  }

  /**
   * The two terminal states the old page already handled
   * (sections/image-upload.liquid:863): an approved book cannot be changed, and a book
   * with an open designer request is waiting on a human.
   */
  function applyOrderState(data) {
    if (data.is_approved) {
      markApprovedInUi();
      return;
    }

    $('tmt-rev-actions').hidden = false;

    // The old page disabled both buttons here (sections/image-upload.liquid:869), and the
    // server agrees: approve-order rejects a preview that is already flagged rejected
    // ("Preview is already rejected or approved"). Leaving Approve live would offer the
    // customer a button that can only fail.
    if (data.is_rejected) {
      $('tmt-rev-open-request').disabled = true;
      $('tmt-rev-open-approve').disabled = true;
      hideAllFixButtons();
      showStatus('Our designers are working on your changes. We’ll email you within 24 hours.');
    }
  }

  function markApprovedInUi() {
    document.body.classList.add('tmt-rev-approved');
    $('tmt-rev-actions').hidden = true;
    $('tmt-rev-approved-bar').hidden = false;
    hideAllFixButtons();
  }

  function hideAllFixButtons() {
    Array.prototype.forEach.call(document.querySelectorAll('.tmt-rev-card__fix'), function (btn) {
      btn.hidden = true;
    });
  }

  /**
   * Shown while a whole-book revision is being drawn, when the customer reloads the link
   * they already had. Nothing to act on yet, so the action bar stays away and the copy
   * points at the email that will carry the new link.
   */
  function showRegenerating() {
    $('tmt-rev-pages').innerHTML =
      '<p class="tmt-rev-expired">' +
      'We’re redrawing your book with the changes you asked for. ' +
      'It usually takes about ten minutes, and we’ll email you a link as soon as it’s ready. ' +
      'You can close this page.' +
      '</p>';
    $('tmt-rev-actions').hidden = true;
  }

  function showLoadFailure(isExpired) {
    $('tmt-rev-pages').innerHTML =
      '<p class="tmt-rev-expired">' +
      (isExpired
        ? 'This preview has expired. Please reply to your order email and we’ll send you a new link.'
        : 'We couldn’t load your book right now. Please refresh the page, or reply to your order email and we’ll help.') +
      '</p>';
    $('tmt-rev-actions').hidden = true;
  }

  /* -------------------------------------------------------------- render */

  function renderPages(urls) {
    var container = $('tmt-rev-pages');
    container.innerHTML = '';

    var total = urls.length;

    urls.forEach(function (url, i) {
      var n = i + 1;
      var card = document.createElement('div');
      card.className = 'tmt-rev-card';
      card.id = 'tmt-rev-card-' + n;
      card.innerHTML =
        '<p class="tmt-rev-card__label"><span>' +
          (n === 1 ? 'Cover · Page 1 of ' + total : 'Page ' + n + ' of ' + total) +
        '</span><span class="tmt-rev-card__badge">★ Updated!</span></p>' +
        '<button type="button" class="tmt-rev-card__img-btn" aria-label="See page ' + n + ' bigger">' +
          '<img class="tmt-rev-card__img" alt="Book page ' + n + '">' +
          '<span class="tmt-rev-card__hint">Tap to enlarge</span>' +
          '<span class="tmt-rev-card__overlay"><span class="tmt-rev-spinner" aria-hidden="true"></span>' +
          '<span class="tmt-rev-card__overlay-text">Redrawing…</span></span>' +
        '</button>' +
        // No Fix button on a page with no child on it (dedication, back cover): there is
        // nothing there to redraw, so the request would spend one of the three changes and
        // return the identical image.
        (selfServiceAvailable() && !R.isStaticPage(url)
          ? '<button type="button" class="tmt-rev-card__fix" data-page="' + n + '">Fix this page</button>'
          : '');

      // Assigned rather than interpolated into the HTML: a presigned URL carries a
      // signature whose characters must not be re-encoded on the way through innerHTML.
      card.querySelector('.tmt-rev-card__img').src = url;
      card.querySelector('.tmt-rev-card__img-btn').addEventListener('click', function () {
        openZoom(i);
      });

      container.appendChild(card);
    });
  }

  function setCardBusy(pageNumber, busy, message) {
    var card = $('tmt-rev-card-' + pageNumber);
    if (!card) return;

    card.classList.toggle('is-redrawing', Boolean(busy));

    var fixBtn = card.querySelector('.tmt-rev-card__fix');
    if (fixBtn) fixBtn.disabled = Boolean(busy);

    if (message) card.querySelector('.tmt-rev-card__overlay-text').textContent = message;
    if (busy) card.classList.remove('is-updated');
  }

  function markUpdated(pageNumbers) {
    pageNumbers.forEach(function (n) {
      var card = $('tmt-rev-card-' + n);
      if (!card) return;

      card.querySelector('.tmt-rev-card__img').src = state.pageUrls[n - 1];
      card.classList.remove('is-redrawing');
      card.classList.add('is-updated');

      var fixBtn = card.querySelector('.tmt-rev-card__fix');
      if (fixBtn) fixBtn.disabled = false;
    });
  }

  function showStatus(html) {
    $('tmt-rev-status-text').innerHTML = html;
    $('tmt-rev-status').classList.add('is-visible');
  }

  function hideStatus() {
    $('tmt-rev-status').classList.remove('is-visible');
  }

  /* -------------------------------------------------------------- modals */

  function openModal(backdrop) {
    state.lastFocus = document.activeElement;
    backdrop.classList.add('is-open');
    state.openBackdrop = backdrop;

    var target = backdrop.querySelector(
      '.tmt-rev-zoom__close, .tmt-rev-choice:not([hidden]), .tmt-rev-btn--primary'
    );
    if (target) target.focus();

    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!state.openBackdrop) return;

    state.openBackdrop.classList.remove('is-open');
    state.openBackdrop = null;
    document.body.style.overflow = '';

    if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus();
  }

  function bindModalDismissal() {
    Array.prototype.forEach.call(document.querySelectorAll('#tmt-revision .tmt-rev-backdrop'), function (bd) {
      bd.addEventListener('click', function (e) {
        if (e.target === bd) closeModal();
      });
      Array.prototype.forEach.call(bd.querySelectorAll('[data-close]'), function (btn) {
        btn.addEventListener('click', closeModal);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
  }

  /* ---------------------------------------------------------------- zoom */

  function openZoom(index) {
    state.zoomIndex = index;
    renderZoom();
    openModal($('tmt-rev-zoom'));
  }

  function renderZoom() {
    var total = state.pageUrls.length;

    $('tmt-rev-zoom-img').src = state.pageUrls[state.zoomIndex];
    $('tmt-rev-zoom-img').alt = 'Book page ' + (state.zoomIndex + 1) + ', enlarged';
    $('tmt-rev-zoom-title').textContent =
      (state.zoomIndex === 0 ? 'Cover · ' : '') + 'Page ' + (state.zoomIndex + 1) + ' of ' + total;
    $('tmt-rev-zoom-prev').disabled = state.zoomIndex === 0;
    $('tmt-rev-zoom-next').disabled = state.zoomIndex === total - 1;
  }

  function bindZoom() {
    $('tmt-rev-zoom-prev').addEventListener('click', function () {
      if (state.zoomIndex > 0) {
        state.zoomIndex--;
        renderZoom();
      }
    });
    $('tmt-rev-zoom-next').addEventListener('click', function () {
      if (state.zoomIndex < state.pageUrls.length - 1) {
        state.zoomIndex++;
        renderZoom();
      }
    });
  }

  /* ------------------------------------------------------------- chooser */

  function bindChooser() {
    $('tmt-rev-open-request').addEventListener('click', function () {
      // With self-service off there is only one option, so the chooser is a dead step
      // and the customer goes straight to the form.
      if (!selfServiceAvailable()) {
        openDesignerRequest();
        return;
      }
      $('tmt-rev-choose-self').hidden = false;
      openModal($('tmt-rev-choose'));
    });

    $('tmt-rev-choose-designers').addEventListener('click', function () {
      closeModal();
      openDesignerRequest();
    });

    $('tmt-rev-choose-self').addEventListener('click', function () {
      closeModal();
      openWholeBook();
    });
  }

  /* ---------------------------------------------- request changes (staff) */

  function openDesignerRequest() {
    $('tmt-rev-request-text').value = '';
    $('tmt-rev-request-counter').textContent = '0/500';
    $('tmt-rev-request-error').classList.remove('is-shown');
    $('tmt-rev-request-form').hidden = false;
    $('tmt-rev-request-success').hidden = true;
    openModal($('tmt-rev-request'));
  }

  /**
   * Unchanged from the old page (sections/image-upload.liquid:1015): a single POST to
   * send-request-changes, which emails support, appends the "[Task ID:...]" order note
   * and flips the preview to rejected. The old code also called approveOrder(..., false),
   * but that helper is a no-op unless isApproved is true (image-upload.liquid:3381), so
   * there is deliberately no second request here.
   */
  function submitDesignerRequest(message) {
    var preview = state.preview;

    return fetch(state.apiBase + 'send-request-changes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_email: preview.email,
        client_id: state.clientId,
        order_id: parseInt(preview.order_id, 10),
        order_number: preview.order_number,
        message: message
      })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          throw new Error(data.error || 'Failed to submit your request. Please try again.');
        });
      }
      return res.json().catch(function () { return {}; });
    });
  }

  function bindDesignerRequest() {
    var textarea = $('tmt-rev-request-text');
    var counter = $('tmt-rev-request-counter');
    var sendBtn = $('tmt-rev-request-send');

    textarea.addEventListener('input', function () {
      counter.textContent = textarea.value.length + '/500';
      if (textarea.value.trim()) $('tmt-rev-request-error').classList.remove('is-shown');
    });

    sendBtn.addEventListener('click', function () {
      if (!textarea.value.trim()) {
        $('tmt-rev-request-error').classList.add('is-shown');
        textarea.focus();
        return;
      }

      sendBtn.disabled = true;
      sendBtn.textContent = 'Sending…';

      submitDesignerRequest(textarea.value.trim())
        .then(function () {
          $('tmt-rev-request-form').hidden = true;
          $('tmt-rev-request-success').hidden = false;
          $('tmt-rev-open-request').disabled = true;
          hideAllFixButtons();
        })
        .catch(function (err) {
          toast(err.message || 'Something went wrong.', true);
        })
        .then(function () {
          sendBtn.disabled = false;
          sendBtn.textContent = 'Send';
        });
    });
  }

  /* ------------------------------------------------------------- approve */

  function bindApprove() {
    $('tmt-rev-open-approve').addEventListener('click', function () {
      $('tmt-rev-approve-confirm').hidden = false;
      $('tmt-rev-approve-success').hidden = true;
      openModal($('tmt-rev-approve'));
    });

    var yesBtn = $('tmt-rev-approve-yes');

    yesBtn.addEventListener('click', function () {
      yesBtn.disabled = true;
      yesBtn.textContent = 'Approving…';

      // Same call the old page made (sections/image-upload.liquid:3383): the body is the
      // client id and nothing else, so the server resolves the order itself.
      fetch(state.apiBase + 'approve-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: state.clientId })
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (data) {
              throw new Error(data.error || data.message || 'An error when approving the order. Please try again.');
            });
          }

          $('tmt-rev-approve-confirm').hidden = true;
          $('tmt-rev-approve-success').hidden = false;
          markApprovedInUi();
          advanceProgressToPrinting();
        })
        .catch(function (err) {
          toast(err.message, true);
        })
        .then(function () {
          yesBtn.disabled = false;
          yesBtn.textContent = 'Yes — print my book';
        });
    });
  }

  function advanceProgressToPrinting() {
    var review = $('tmt-rev-step-review');
    review.classList.remove('is-current');
    review.classList.add('is-done');
    review.querySelector('.tmt-rev-progress__dot').textContent = '✓';

    var here = review.querySelector('.tmt-rev-progress__here');
    if (here) here.remove();

    $('tmt-rev-step-print').classList.add('is-current');
  }

  /* ------------------------------------------- self-service: whole book */

  /**
   * The self-service flows need revision-page.js to build their payloads. If that asset
   * failed to load there is nothing to submit, so the page falls back to what it could
   * always do: ask the designers, and approve.
   */
  function selfServiceAvailable() {
    return Boolean(R);
  }

  /* Swatch artwork, ported verbatim from the client's mockup
     (.claude/features/customer-revision-page/design-reference.html): a tinted iris and a
     hair tuft, drawn rather than photographed so every colour reads the same way. */
  function hexToRgb(hex) {
    var n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function shade(hex, amount) {
    var rgb = hexToRgb(hex).map(function (c) {
      var v = amount >= 0 ? c + (255 - c) * amount : c * (1 + amount);
      return Math.round(Math.min(255, Math.max(0, v)));
    });
    return 'rgb(' + rgb.join(',') + ')';
  }

  function irisSVG(color) {
    var light = shade(color, 0.5);
    var dark = shade(color, -0.45);
    var streaks = '';

    for (var k = 0; k < 30; k++) {
      var a = (k / 30) * Math.PI * 2 + 0.1;
      var isLight = k % 2 === 0;
      var r1 = 9;
      var r2 = isLight ? 22.8 : 21;
      streaks +=
        '<line x1="' + (25 + Math.cos(a) * r1).toFixed(1) + '" y1="' + (25 + Math.sin(a) * r1).toFixed(1) +
        '" x2="' + (25 + Math.cos(a) * r2).toFixed(1) + '" y2="' + (25 + Math.sin(a) * r2).toFixed(1) +
        '" stroke="' + (isLight ? light : dark) + '" stroke-width="' + (isLight ? 1.7 : 1.3) +
        '" stroke-linecap="round"/>';
    }

    return '<svg viewBox="0 0 50 50" aria-hidden="true" focusable="false">' +
      '<circle cx="25" cy="25" r="24" fill="' + color + '"/>' + streaks +
      '<circle cx="25" cy="25" r="24" fill="none" stroke="' + dark + '" stroke-width="1.6"/>' +
      '<circle cx="25" cy="25" r="9.5" fill="#121212"/>' +
      '<circle cx="29.5" cy="19.5" r="4" fill="#FFFFFF"/>' +
      '</svg>';
  }

  function hairSVG(color) {
    var lum = hexToRgb(color).reduce(function (a, c) { return a + c; }, 0) / (3 * 255);
    var strand = lum < 0.25 ? shade(color, 0.38) : shade(color, -0.38);
    var strandAttrs = 'fill="none" stroke="' + strand + '" stroke-width="1.6" stroke-linecap="round"';

    return '<svg viewBox="0 0 50 50" aria-hidden="true" focusable="false">' +
      '<path fill="' + color + '" d="M25 7 C15 7 9.5 15 10 26 C10.3 32 8.5 37 5.5 40.5 C11 41.5 15 39 17.2 35.5 C17.8 39.5 16.5 43.5 14 46 C20 45.2 23.6 41 24.6 36.2 C25.8 41 29.4 45.2 35.5 46 C33 43.5 31.7 39.5 32.3 35.5 C34.5 39 38.5 41.5 44 40.5 C41 37 39.2 32 39.5 26 C40 15 35 7 25 7 Z"/>' +
      '<path ' + strandAttrs + ' d="M19.5 11.5 C15.5 15 13.8 20.5 14.3 27.5"/>' +
      '<path ' + strandAttrs + ' d="M25 10.5 C24.2 17 24.2 25 25 32.5"/>' +
      '<path ' + strandAttrs + ' d="M30.5 11.5 C34.5 15 36.2 20.5 35.7 27.5"/>' +
      '</svg>';
  }

  /**
   * Single-select, and clicking the selected colour clears it - that is how a customer
   * says "actually, leave the hair as it is", which the server reads as "omit this trait
   * from the prompt and keep what the artwork already has".
   */
  function buildSwatches(container, options, kind, onChange) {
    container.innerHTML = '';

    options.forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tmt-rev-swatch';
      btn.setAttribute('aria-pressed', 'false');
      btn.dataset.value = opt.label;
      btn.innerHTML =
        '<span class="tmt-rev-swatch__dot' + (kind === 'hair' ? ' tmt-rev-swatch__dot--hair' : '') + '">' +
          (kind === 'hair' ? hairSVG(opt.color) : irisSVG(opt.color)) +
          '<span class="tmt-rev-swatch__check" aria-hidden="true">\u2713</span>' +
        '</span>' +
        '<span class="tmt-rev-swatch__name">' + opt.label + '</span>';

      btn.addEventListener('click', function () {
        var wasOn = btn.getAttribute('aria-pressed') === 'true';
        Array.prototype.forEach.call(container.querySelectorAll('.tmt-rev-swatch'), function (other) {
          other.setAttribute('aria-pressed', 'false');
        });
        if (!wasOn) btn.setAttribute('aria-pressed', 'true');
        if (onChange) onChange();
      });

      container.appendChild(btn);
    });
  }

  function selectedSwatch(container) {
    var on = container.querySelector('.tmt-rev-swatch[aria-pressed="true"]');
    return on ? on.dataset.value : null;
  }

  function clearSwatches(container) {
    Array.prototype.forEach.call(container.querySelectorAll('.tmt-rev-swatch'), function (btn) {
      btn.setAttribute('aria-pressed', 'false');
    });
  }

  var wholePending = null;

  function showWholeStep(step) {
    $('tmt-rev-whole-form').hidden = step !== 'form';
    $('tmt-rev-whole-confirm-step').hidden = step !== 'confirm';
    $('tmt-rev-whole-success').hidden = step !== 'success';
  }

  function currentWholeSelection() {
    return {
      clientId: state.clientId,
      hair: selectedSwatch($('tmt-rev-whole-hair')),
      eyes: selectedSwatch($('tmt-rev-whole-eyes')),
      name: $('tmt-rev-whole-name').value,
      currentName: (state.preview && state.preview.child_name) || ''
    };
  }

  function openWholeBook() {
    clearSwatches($('tmt-rev-whole-hair'));
    clearSwatches($('tmt-rev-whole-eyes'));
    $('tmt-rev-whole-name').value = (state.preview && state.preview.child_name) || '';
    showWholeStep('form');
    openModal($('tmt-rev-whole'));
  }

  function bindWholeBook() {
    if (!R) return;

    buildSwatches($('tmt-rev-whole-hair'), R.HAIR_OPTIONS, 'hair');
    buildSwatches($('tmt-rev-whole-eyes'), R.EYE_OPTIONS, 'eye');

    // Step one only assembles the payload. Redrawing replaces every page, so it gets an
    // explicit "are you sure" rather than firing on the first click.
    //
    // Submitting with nothing chosen is allowed: the generator is told to keep the colours it
    // reads off the photo, so an empty selection means "draw it again", which is a reasonable
    // thing to want when a book simply came out badly.
    $('tmt-rev-whole-confirm').addEventListener('click', function () {
      var selection = currentWholeSelection();
      wholePending = R.buildBookPayload(selection);

      var parts = [];
      var described = R.describeSelection(selection);
      if (described) parts.push(described);
      if (wholePending.name) parts.push('the name \u201c' + wholePending.name + '\u201d');

      $('tmt-rev-whole-confirm-text').textContent =
        'This replaces all ' + state.pageUrls.length + ' pages ' +
        (parts.length ? 'with ' + parts.join(' and ') : 'and keeps the same colors and name') +
        ' \u2014 ' + (state.settings.bookEta || 'it takes about 10 minutes, and we\u2019ll email you.');

      showWholeStep('confirm');
      $('tmt-rev-whole-confirm-yes').focus();
    });

    $('tmt-rev-whole-confirm-back').addEventListener('click', function () {
      showWholeStep('form');
    });

    $('tmt-rev-whole-confirm-yes').addEventListener('click', function () {
      var btn = $('tmt-rev-whole-confirm-yes');
      btn.disabled = true;
      btn.textContent = 'Sending\u2026';

      postRevision('revision/book', wholePending)
        .then(function (data) {
          showWholeStep('success');

          if (wholePending.name) applyChildName(wholePending.name);

          showStatus(
            'Redrawing <strong>the whole book</strong> \u2014 ' +
            (state.settings.bookEta || 'about 10 minutes, we\u2019ll email you.')
          );

          for (var n = 1; n <= state.pageUrls.length; n++) setCardBusy(n, true, 'Redrawing\u2026');

          // Nothing can be approved or re-requested while every page is being replaced.
          $('tmt-rev-actions').hidden = true;

          pollForNewBook(data.new_client_id);
        })
        .catch(function (err) {
          toast(err.message, true);
          showWholeStep('form');
        })
        .then(function () {
          btn.disabled = false;
          btn.textContent = 'Yes \u2014 redraw all pages';
        });
    });
  }

  /* ------------------------------------------------------- fix one page */

  var fixPage = null;

  function openFix(pageNumber) {
    fixPage = pageNumber;
    $('tmt-rev-fix-page-num').textContent = pageNumber;
    clearSwatches($('tmt-rev-fix-hair'));
    clearSwatches($('tmt-rev-fix-eyes'));
    $('tmt-rev-fix-form').hidden = false;
    $('tmt-rev-fix-success').hidden = true;
    openModal($('tmt-rev-fix'));
  }

  function bindFix() {
    // Same guard as bindWholeBook: without R there are no swatch options to read, and the
    // dereference below throws inside init(), leaving the whole revision page blank rather
    // than just this one flow unavailable.
    if (!R) return;

    buildSwatches($('tmt-rev-fix-hair'), R.HAIR_OPTIONS, 'hair');
    buildSwatches($('tmt-rev-fix-eyes'), R.EYE_OPTIONS, 'eye');

    // Delegated: cards are re-rendered whenever the book reloads.
    $('tmt-rev-pages').addEventListener('click', function (e) {
      var btn = e.target.closest('.tmt-rev-card__fix');
      if (btn) openFix(Number(btn.dataset.page));
    });

    // Choosing no colour at all is a valid request: it redraws the page and keeps the hair and
    // eyes the generator reads off the photo. Customers mostly reach for this button because one
    // page came out oddly, not because they want a different colour.
    $('tmt-rev-fix-confirm').addEventListener('click', function () {
      var hair = selectedSwatch($('tmt-rev-fix-hair'));
      var eyes = selectedSwatch($('tmt-rev-fix-eyes'));

      var payload = R.buildPagePayload({
        clientId: state.clientId, pageNumber: fixPage, hair: hair, eyes: eyes
      });
      var intent = R.describeIntent({ hair: hair, eyes: eyes });
      var btn = $('tmt-rev-fix-confirm');
      btn.disabled = true;

      postRevision('revision/page', payload)
        .then(function () {
          $('tmt-rev-fix-success-text').textContent =
            'Redrawing page ' + fixPage + ' ' + intent + ' - ' + (state.settings.pageEta || 'about 2 minutes.');
          $('tmt-rev-fix-form').hidden = true;
          $('tmt-rev-fix-success').hidden = false;
          setCardBusy(fixPage, true, 'Redrawing page ' + fixPage + ' ' + intent + '...');
          // Approve has to go while a page is being redrawn, the same way the whole-book
          // flow hides it. The server refuses an approval mid-revision with a 409, so
          // leaving the button up only buys the customer an error toast - and before that
          // guard existed it sent the pre-fix artwork to print. Restored by the poller.
          $('tmt-rev-actions').hidden = true;
          pollForPageUpdates();
        })
        .catch(function (err) { toast(err.message, true); })
        .then(function () { btn.disabled = false; });
    });
  }

  /**
   * A regenerated page comes back under the same client_id, so the only signal that it
   * landed is the URL for that page changing. Poll the same endpoint the page loaded
   * from and diff.
   */
  function pollForPageUpdates() {
    var attempts = 0;
    var maxAttempts = 48;           // 48 x 10s = 8 minutes, matching the server's timeout
    var previous = state.pageUrls.slice();
    var busyPage = fixPage;         // captured: the customer may open another card meanwhile

    var timer = setInterval(function () {
      attempts++;
      fetch(state.apiBase + 'preview-images?client_id=' + encodeURIComponent(state.clientId))
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (!data || !Array.isArray(data.image_urls)) return;
          var changed = R.diffPageUrls(previous, data.image_urls);
          if (changed.length === 0) return;
          state.pageUrls = data.image_urls;
          markUpdated(changed);
          clearInterval(timer);
          $('tmt-rev-actions').hidden = false;
        })
        .catch(function () { /* a dropped poll is not an error; the next one retries */ });

      if (attempts >= maxAttempts) {
        clearInterval(timer);
        setCardBusy(busyPage, false);
        // Approve comes back even on timeout: the server's own 30-minute pending window is
        // the real guard, and leaving the customer with no action at all is worse.
        $('tmt-rev-actions').hidden = false;
        // No email is sent for a single-page fix - only the whole-book flow emails - so this
        // says to come back rather than promising something that never arrives.
        toast('This is taking longer than usual. Please check back in a few minutes.', true);
      }
    }, 10000);
  }

  function postRevision(path, payload) {
    return fetch(state.apiBase + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        // Every 4xx from the revision endpoints carries a message written to be shown to
        // the customer as-is (see utils/revisionGuards.js on the server).
        if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
        return data;
      });
    });
  }

  /**
   * A regenerated book lands under a NEW client_id - a different preview row - so this
   * waits for that row to appear and then moves the page onto it. The emailed link points
   * at the same id, so a later refresh shows exactly what is on screen now.
   */
  function pollForNewBook(newClientId) {
    if (!newClientId) return;

    var attempts = 0;
    var maxAttempts = 90;            // 90 x 10s = 15 minutes; the email is the real backstop

    var timer = setInterval(function () {
      attempts++;

      fetch(state.apiBase + 'preview-images?client_id=' + encodeURIComponent(newClientId))
        .then(function (res) { return res.ok ? res.json() : null; })   // 404s until the job finishes
        .then(function (data) {
          if (!data || !Array.isArray(data.image_urls) || data.image_urls.length === 0) return;

          clearInterval(timer);

          state.clientId = newClientId;
          state.preview = data;
          state.pageUrls = data.image_urls;

          var url = new URL(window.location.href);
          url.searchParams.set('client-id', newClientId);
          window.history.replaceState({}, '', url.toString());

          applyChildName(data.child_name);
          renderPages(state.pageUrls);
          markUpdated(state.pageUrls.map(function (_, i) { return i + 1; }));
          hideStatus();
          applyOrderState(data);
        })
        .catch(function () { /* a dropped poll is not an error; the next one retries */ });

      if (attempts >= maxAttempts) {
        clearInterval(timer);
        hideStatus();
        toast('This is taking longer than usual. We\u2019ll email you as soon as your book is ready.', true);
      }
    }, 10000);
  }

  /* --------------------------------------------------------------- toast */

  // Reuses #success-popup / #error-popup, which already exist in
  // sections/image-upload.liquid and are shared with the pre-purchase flow. Do not delete
  // them when the legacy revision markup is retired.
  function toast(message, isError) {
    var popup = document.getElementById(isError ? 'error-popup' : 'success-popup');
    if (!popup) return;

    var msg = popup.querySelector('.success-popup-message');
    if (msg) msg.textContent = message;

    popup.style.display = 'block';
    popup.style.opacity = '1';

    if (window._tmtRevToastTimeout) clearTimeout(window._tmtRevToastTimeout);
    window._tmtRevToastTimeout = setTimeout(function () {
      popup.style.opacity = '0';
      setTimeout(function () { popup.style.display = 'none'; }, 400);
    }, 4000);
  }

  window.RevisionPageUI = {
    init: init,
    state: state,
    loadPreview: loadPreview,
    renderPages: renderPages,
    markUpdated: markUpdated,
    setCardBusy: setCardBusy,
    showStatus: showStatus,
    hideStatus: hideStatus,
    openModal: openModal,
    closeModal: closeModal,
    toast: toast
  };
})();
